import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Fingerprint, Loader2, ChevronRight } from "lucide-react";
import { API, api } from "@/lib/api";
import { Card } from "./bits";

export const DuplicateAlertsCard = () => {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`${API}/admin/alerts/duplicates`).then((r) => setData(r.data)).catch(() => setData({ alerts: [], error: true }));
  }, []);

  if (!data) return null;
  const alerts = data.alerts || [];
  return (
    <Card className={`p-4 ${alerts.length ? "border-amber-200 bg-amber-50/40" : ""}`} data-testid="sa-dup-alerts-card">
      <div className="flex items-center gap-2 mb-1">
        <Copy size={14} className={alerts.length ? "text-amber-600" : "text-gray-300"} />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Alertes doublons de plaque</h3>
        {alerts.length > 0 && <span className="text-[10px] font-bold text-amber-700 bg-amber-100 rounded-full px-2 py-0.5">{alerts.length}</span>}
      </div>
      <p className="text-[10px] text-gray-400 mb-2">Signalement uniquement — aucune fusion ni correction automatique. Traitement humain via la fiche client → Réconciliation.</p>
      {alerts.length === 0 ? (
        <p className="text-xs text-gray-400" data-testid="sa-dup-alerts-empty">{data.error ? "Alertes indisponibles" : "Aucune plaque en double dans les registres clients."}</p>
      ) : alerts.map((a, i) => (
        <div key={`${a.tenant}-${a.reg_number}-${i}`}
          className="flex flex-wrap items-center gap-3 py-2 border-b border-amber-100/60 last:border-0 cursor-pointer hover:bg-amber-50 rounded-lg px-2"
          onClick={() => navigate(`/super-admin/clients/${a.client_id}`)}
          data-testid={`sa-dup-alert-${a.tenant}-${i}`}>
          <span className="text-xs font-semibold text-gray-800">{a.client_name} <span className="text-gray-400 font-normal">({a.tenant})</span></span>
          <span className="text-xs font-bold text-amber-700">{a.reg_number}</span>
          <div className="flex-1 min-w-[220px]">
            {a.vehicles.map((v) => (
              <div key={v.uid} className="text-[11px] text-gray-500">
                {v.label || v.uid} · uid {v.uid.slice(0, 12)}… · boîtier {v.tracker_id ?? "—"} · {v.status}
                {v.has_admin_record ? " · fiche admin" : ""}
              </div>
            ))}
          </div>
          <ChevronRight size={14} className="text-amber-300" />
        </div>
      ))}
    </Card>
  );
};

export const UidMigrationCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`${API}/admin/uid-migration`).then((r) => setData(r.data)).catch(() => setData({ tenants: [], error: true }));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const toggle = async (enabled) => {
    if (!enabled && !window.confirm(
      "Rollback S3 : toutes les lectures repasseront uniquement par tracker_id (comportement pré-migration). Aucun mapping n'est supprimé. Continuer ?")) return;
    setBusy(true);
    try { await api.put(`${API}/admin/uid-migration`, { enabled }); load(); } catch (e) { console.error(e); }
    setBusy(false);
  };

  return (
    <Card className="p-4" data-testid="sa-uid-migration-card">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <Fingerprint size={14} className="text-[#1e6ae5]" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Migration identité véhicule (S3 — lectures uid-first)</h3>
        <label className="ml-auto flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          {busy && <Loader2 size={12} className="animate-spin" />}
          <input type="checkbox" checked={!!data.enabled} disabled={busy} onChange={(e) => toggle(e.target.checked)} data-testid="sa-uid-toggle" />
          Lectures uid-first {data.enabled ? "activées" : "désactivées (rollback tracker_id)"}
        </label>
      </div>
      <p className="text-[10px] text-gray-400 mb-2">
        Fiches et documents lus via l'identité stable vehicle_uid, tracker_id en secours temporaire.
        Chaque lecture de secours est mesurée et auditée — objectif : 0 lecture dépendante du tracker_id.
        Barres : évolution jour par jour sur 14 jours (ambre = lectures en secours, vert = 100 % uid).
      </p>
      {(data.tenants || []).length === 0 ? (
        <p className="text-xs text-gray-400" data-testid="sa-uid-empty">Aucune lecture mesurée sur les 7 derniers jours.</p>
      ) : (
        <div className="space-y-1.5">
          {data.tenants.map((t) => (
            <div key={t.tenant} className="flex flex-wrap items-center gap-3 text-[11px]" data-testid={`sa-uid-tenant-${t.tenant}`}>
              <span className="font-semibold text-gray-700 min-w-[90px]">{t.tenant}</span>
              <span className="text-emerald-700">{t.uid_7d} lecture(s) via uid</span>
              <span className={t.fallback_7d ? "text-amber-600 font-medium" : "text-gray-400"}>
                {t.fallback_7d} en secours tracker_id
              </span>
              {t.fallback_tracker_ids?.length > 0 && (
                <span className="text-gray-400">boîtiers : {t.fallback_tracker_ids.slice(0, 8).join(", ")}{t.fallback_tracker_ids.length > 8 ? "…" : ""}</span>
              )}
              {(t.days || []).length > 0 && (
                <span className="flex items-end gap-[2px] ml-auto" data-testid={`sa-uid-days-${t.tenant}`}>
                  {t.days.map((d) => (
                    <span key={d.day} title={`${d.day} : ${d.uid} via uid / ${d.fallback} en secours`}
                      className={`w-1.5 rounded-sm ${d.fallback ? "bg-amber-400" : "bg-emerald-300"}`}
                      style={{ height: `${5 + Math.min(14, d.fallback * 2)}px` }} />
                  ))}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
